import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getService, saveService, deleteService, getAllDocuments, getAllEmailTemplates, getAllSubdomains } from '../services/api.service';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import Helper from '../services/helper.service';
import { useConfirm } from '../components/confirm-dialog';
import { FaChevronLeft, FaSearch } from 'react-icons/fa';
import FieldSelector from '../components/fields-selectors';

const langs = ['fr', 'en', 'de'];
const flags = {
    fr: '/assets/images/fr.png',
    en: '/assets/images/en.png',
    de: '/assets/images/de.png',
};

const NO_SUBDOMAIN_KEY = '__NO_SUBDOMAIN__';

const getSubdomainValue = (subdomain) =>
    subdomain?.subdomain ||
    subdomain?.key ||
    subdomain?.slug ||
    subdomain?.name ||
    subdomain?.domain ||
    subdomain?._id ||
    '';

const getSubdomainLabel = (subdomain) =>
    subdomain?.fullName ||
    subdomain?.subdomain ||
    subdomain?.label ||
    subdomain?.name ||
    subdomain?.domain ||
    subdomain?.key ||
    subdomain?.slug ||
    subdomain?._id ||
    '(Sans nom)';

const defaultService = {
    title: {}, shortDescription: {}, description: {},
    price: 0, statutProduct: 'available',
    contactTo: '', enable: true, requiredDocuments: [],
    requiredFields: [],
    emailTemplates: {}
};
const normalizeI18n = (obj) => langs.reduce((acc, l) => ({ ...acc, [l]: obj?.[l] || '' }), {});

const emailOK = (v) => !v || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

const ServiceEditor = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id && id !== 'new');

    const [service, setService] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [emailTemplates, setEmailTemplates] = useState([]);
    const [activeTab, setActiveTab] = useState('general'); // 'general' | 'files'
    const [lang, setLang] = useState('fr');
    const [docSearch, setDocSearch] = useState('');
    const [subdomains, setSubdomains] = useState([]);
    const confirm = useConfirm();

    useEffect(() => {
        getAllDocuments()
            .then(setDocuments)
            .catch(() => Helper.toast("Impossible de charger les documents", "error"));

        getAllEmailTemplates()
            .then(setEmailTemplates)
            .catch(() => Helper.toast("Impossible de charger les modèles d'email", "error"));

        getAllSubdomains()
            .then(setSubdomains)
            .catch(() => Helper.toast("Impossible de charger les sous-domaines", "error"));
    }, []);

    useEffect(() => {
        if (isEdit) {
            getService(id)
                .then((data) =>
                    setService({
                        ...data,
                        title: normalizeI18n(data.title),
                        shortDescription: normalizeI18n(data.shortDescription),
                        description: normalizeI18n(data.description),
                        requiredDocuments: (data.requiredDocuments || []).map((doc) => ({
                            ...doc,
                            documentId: doc.documentId?._id || doc.documentId,
                            mandatory: Boolean(doc.mandatory),
                            subdomains: Array.isArray(doc.subdomains) ? doc.subdomains : [],
                        })),
                        requiredFields: (data.requiredFields || []).map((field) => ({
                            ...field,
                            subdomains: Array.isArray(field.subdomains) ? field.subdomains : [],
                        })),
                        emailTemplates: data.emailTemplates || {}
                    })
                )
                .catch(() => {
                    Helper.toast("Impossible de charger le service", "error");
                    navigate('/services');
                });
        } else {
            setService({
                ...defaultService,
                title: normalizeI18n(defaultService.title),
                shortDescription: normalizeI18n(defaultService.shortDescription),
                description: normalizeI18n(defaultService.description),
            });
        }
    }, [id, isEdit, navigate]);

    const updateField = (field, value) => setService((s) => ({ ...s, [field]: value }));
    const updateNested = (section, l, value) =>
        setService((s) => ({ ...s, [section]: { ...s[section], [l]: value } }));

    const docIdOf = (doc) => String(doc.documentId?._id || doc.documentId);

    const toggleDoc = (id) =>
        setService((s) => {
            const found = (s.requiredDocuments || []).find((d) => docIdOf(d) === String(id));

            return {
                ...s,
                requiredDocuments: found
                    ? s.requiredDocuments.filter((d) => docIdOf(d) !== String(id))
                    : [
                        ...(s.requiredDocuments || []),
                        {
                            documentId: id,
                            mandatory: false,
                            subdomains: [],
                        },
                    ],
            };
        });

    const updateRequiredDoc = (documentId, patch) =>
        setService((s) => ({
            ...s,
            requiredDocuments: (s.requiredDocuments || []).map((doc) =>
                docIdOf(doc) === String(documentId)
                    ? { ...doc, ...patch }
                    : doc
            ),
        }));

    const toggleRequiredDocSubdomain = (documentId, subdomainValue) =>
        setService((s) => ({
            ...s,
            requiredDocuments: (s.requiredDocuments || []).map((doc) => {
                if (docIdOf(doc) !== String(documentId)) return doc;

                const current = Array.isArray(doc.subdomains) ? doc.subdomains : [];
                const exists = current.includes(subdomainValue);

                return {
                    ...doc,
                    subdomains: exists
                        ? current.filter((v) => v !== subdomainValue)
                        : [...current, subdomainValue],
                };
            }),
        }));

    const setRequiredDocAllSubdomains = (documentId) =>
        updateRequiredDoc(documentId, { subdomains: [] });

    const handleSave = async () => {
        try {
            const payload = {
                ...service,

                requiredDocuments: (service.requiredDocuments || []).map((doc) => ({
                    documentId: doc.documentId?._id || doc.documentId,
                    conditionalDisplay: doc.conditionalDisplay?.fieldKey ? doc.conditionalDisplay : undefined,
                    mandatory: Boolean(doc.mandatory),
                    subdomains: Array.isArray(doc.subdomains)
                        ? doc.subdomains.filter(Boolean)
                        : [],
                })),

                requiredFields: (service.requiredFields || []).map((field) => ({
                    ...field,
                    conditionalDisplay: field.conditionalDisplay?.fieldKey
                        ? field.conditionalDisplay
                        : undefined,
                    subdomains: Array.isArray(field.subdomains)
                        ? field.subdomains.filter(Boolean)
                        : [],
                })),
            };

            await saveService(payload);
        } catch {
            Helper.toast("Erreur lors de l'enregistrement", "error");
            return;
        }

        navigate('/services');
    };

    const handleDelete = async () => {
        const ok = await confirm({
            title: "Attention !",
            message: <>Supprimer ce service <strong>{service.title?.fr || '(Sans titre)'}</strong> ?</>,
            confirmText: "Oui",
            cancelText: "Non",
            tone: "warning",
        });
        if (!ok) return Helper.toast("Action annulée", "info");

        try {
            await deleteService(service._id);
            navigate("/services");
        } catch {
            Helper.toast("Erreur lors de la suppression", "error");
        }
    };

    const canSave = useMemo(() => {
        return Boolean(service?.title?.fr?.trim()) && emailOK(service?.contactTo);
    }, [service]);

    const t = (obj) => obj?.[lang] || obj?.fr || obj?.en || obj?.de || '';

    const TRIGGERS = [
        'status.created',
        'status.paid',
        'status.in-progress',
        'status.completed',
        'status.rejected',
    ];

    const TRIGGER_LABELS = {
        'status.created': 'À la création',
        'status.paid': 'Quand payé',
        'status.in-progress': 'En cours',
        'status.completed': 'Quand complété',
        'status.rejected': 'Quand rejeté',
    };

    const trigLabel = (k) => (TRIGGER_LABELS[k] ?? TRIGGER_LABELS[k] ?? k);

    // Only show relevant templates in each select: those whose trigger matches this status or 'any'
    const templatesFor = (trigger) =>
        (emailTemplates || []).filter(tpl => (tpl.trigger === trigger || tpl.trigger === 'any'));

    const filteredDocs = useMemo(() => {
        const q = docSearch.trim().toLowerCase();
        if (!q) return documents;
        const pick = (v) => (v?.[lang] || v?.fr || v?.en || v?.de || '').toLowerCase();
        return documents.filter((d) => pick(d.name).includes(q) || pick(d.description).includes(q));
    }, [documents, docSearch, lang]);

    if (!service) return <div className="p-6 text-center text-gray-500">Chargement…</div>;

    const subdomainOptions = [
        {
            value: NO_SUBDOMAIN_KEY,
            label: 'Sans sous-domaine / domaine principal',
        },
        ...(subdomains || [])
            .map((subdomain) => ({
                value: getSubdomainValue(subdomain),
                label: getSubdomainLabel(subdomain),
            }))
            .filter((item) => item.value),
    ];

    return (
        <div className="max-w-6xl mx-auto p-6">
            {/* Sticky Header */}
            <div className="sticky top-0 z-10 mb-6">
                <div className="flex justify-between items-center py-3">
                    <button
                        onClick={() => navigate('/services')}
                        className="inline-flex items-center gap-2 text-gray-600 hover:text-gray-900"
                    >
                        <FaChevronLeft className="h-4 w-4" /> Retour
                    </button>

                    <div className="flex items-center gap-4">
                        {/* language toggle */}
                        <div className="hidden sm:flex items-center gap-1 border rounded-xl bg-white p-1">
                            {langs.map((l) => (
                                <button
                                    key={l}
                                    onClick={() => setLang(l)}
                                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm ${lang === l ? 'bg-gray-900 text-white' : 'text-gray-700 hover:bg-gray-100'
                                        }`}
                                    title={l.toUpperCase()}
                                >
                                    <img src={flags[l]} alt={l} className="w-5 h-4 rounded-sm" />
                                    {l.toUpperCase()}
                                </button>
                            ))}
                        </div>

                        {isEdit && (
                            <button
                                onClick={handleDelete}
                                className="px-4 py-2 rounded border border-red-200 text-red-700 hover:bg-red-50"
                            >
                                Supprimer
                            </button>
                        )}
                        <button
                            onClick={handleSave}
                            disabled={!canSave}
                            className={`px-4 py-2 rounded text-white shadow ${canSave ? 'bg-green-600 hover:bg-green-700' : 'bg-gray-300 cursor-not-allowed'
                                }`}
                        >
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center justify-between mb-4">
                <div className="flex border-b">
                    {['general', 'files', 'fields', 'email'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setActiveTab(tab)}
                            className={`px-4 py-2 -mb-px ${activeTab === tab
                                ? 'border-b-2 border-blue-600 font-semibold text-blue-700'
                                : 'text-gray-500 hover:text-gray-700'
                                }`}
                        >
                            {tab === 'general'
                                ? 'Général'
                                : tab === 'files'
                                    ? 'Fichiers'
                                    : tab === 'fields'
                                        ? 'Champs spéciaux'
                                        : 'Modèle e-mail'}
                        </button>
                    ))}
                </div>

                {/* compact lang toggle on small screens */}
                <div className="sm:hidden">
                    <select
                        value={lang}
                        onChange={(e) => setLang(e.target.value)}
                        className="border rounded px-2 py-1"
                    >
                        {langs.map((l) => (
                            <option key={l} value={l}>{l.toUpperCase()}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* GENERAL */}
            {activeTab === 'general' && (
                <>
                    <div className='rounded-2xl border bg-white shadow-sm p-5 space-y-6 mb-6'>
                        {/* Price / Status / Email */}
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Prix (€)</label>
                                <div className="relative">
                                    <span className="absolute inset-y-0 left-3 flex items-center text-gray-500">€</span>
                                    <input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={service.price}
                                        onChange={(e) => updateField('price', Number(e.target.value))}
                                        className="w-full border rounded pl-7 pr-3 py-2 shadow-sm"
                                        placeholder="0.00"
                                    />
                                </div>
                            </div>

                            <div>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Email de contact</label>
                                <input
                                    type="email"
                                    value={service.contactTo}
                                    onChange={(e) => updateField('contactTo', e.target.value)}
                                    className={`w-full border rounded px-3 py-2 shadow-sm ${emailOK(service.contactTo) ? '' : 'border-red-300'
                                        }`}
                                    placeholder="contact@exemple.com"
                                />
                                {!emailOK(service.contactTo) && (
                                    <p className="mt-1 text-xs text-red-600">Email invalide.</p>
                                )}
                            </div>

                            <div className='text-center'>
                                <label className="block mb-1 text-sm font-medium text-gray-700">Statut</label>
                                <div className="inline-flex rounded-lg border bg-white overflow-hidden">
                                    {[
                                        { v: 'available', label: 'Disponible' },
                                        { v: 'unavailable', label: 'Indisponible' },
                                    ].map((opt) => (
                                        <button
                                            key={opt.v}
                                            type="button"
                                            onClick={() => updateField('statutProduct', opt.v)}
                                            className={`px-3 py-2 text-sm ${service.statutProduct === opt.v
                                                ? 'bg-blue-600 text-white'
                                                : 'hover:bg-gray-100 text-gray-700'
                                                }`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-6">
                        {/* Title */}
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Titre ({lang.toUpperCase()}) <span className="text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                value={service.title[lang]}
                                onChange={(e) => updateNested('title', lang, e.target.value)}
                                className="w-full border rounded px-3 py-2 shadow-sm"
                                placeholder="Nom du service"
                            />
                            {lang === 'fr' && !service.title.fr?.trim() && (
                                <p className="mt-1 text-xs text-red-600">Le titre FR est requis.</p>
                            )}
                        </div>

                        {/* Descriptions (short + full) */}
                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Petite description ({lang.toUpperCase()})
                            </label>
                            <ReactQuill
                                key={`short-${lang}`}
                                theme="snow"
                                value={service.shortDescription[lang]}
                                onChange={(v) => updateNested('shortDescription', lang, v)}
                                className="bg-white rounded shadow"
                            />
                        </div>

                        <div>
                            <label className="block mb-1 text-sm font-medium text-gray-700">
                                Description complète ({lang.toUpperCase()})
                            </label>
                            <ReactQuill
                                key={`full-${lang}`}
                                theme="snow"
                                value={service.description[lang]}
                                onChange={(v) => updateNested('description', lang, v)}
                                className="bg-white rounded shadow"
                            />
                        </div>
                    </div>
                </>
            )}

            {/* FILES (Documents) */}
            {activeTab === 'files' && (
                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-4">
                    <div className="flex items-center justify-between gap-3 flex-wrap">
                        <div className="relative w-full sm:w-80">
                            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                            <input
                                type="text"
                                value={docSearch}
                                onChange={(e) => setDocSearch(e.target.value)}
                                placeholder="Rechercher un document…"
                                className="w-full border rounded pl-9 pr-3 py-2"
                            />
                        </div>
                        <div className="text-sm text-gray-600">
                            Sélectionnés :{' '}
                            <span className="font-semibold">{service.requiredDocuments?.length || 0}</span>
                        </div>
                    </div>

                    {filteredDocs.length === 0 ? (
                        <p className="text-sm text-gray-500 py-6">Aucun document.</p>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {filteredDocs.map((doc) => {
                                const sel = (service.requiredDocuments || []).find((d) => docIdOf(d) === String(doc._id));
                                const selectedSubdomains = Array.isArray(sel?.subdomains) ? sel.subdomains : [];
                                const isAllSubdomains = selectedSubdomains.length === 0;
                                return (
                                    <div
                                        key={doc._id}
                                        className={`cursor-pointer rounded-lg border p-4 transition ${sel
                                            ? 'bg-blue-50 border-blue-500'
                                            : 'bg-gray-50 border-transparent hover:border-gray-200'
                                            }`}
                                        onClick={() => toggleDoc(doc._id)}
                                    >
                                        <div className="flex items-start justify-between gap-3">
                                            <div className="flex-1">
                                                <div className="font-semibold text-gray-800">{t(doc.name)}</div>
                                                <div className="text-sm text-gray-600 line-clamp-2 mt-1">
                                                    {t(doc.description)}
                                                </div>
                                            </div>
                                            <input
                                                type="checkbox"
                                                checked={!!sel}
                                                onChange={() => toggleDoc(doc._id)}
                                                onClick={(e) => e.stopPropagation()}
                                                className="mt-1 h-4 w-4"
                                            />
                                        </div>

                                        {t(doc.link) &&
                                            <div
                                                className="mt-3 flex items-center justify-between gap-3"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <a
                                                    href={t(doc.link)}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    className="text-sm text-blue-600 hover:underline"
                                                >
                                                    Voir le document
                                                </a>
                                            </div>

                                        }
                                        {sel && (
                                            <div
                                                className="mt-4 border-t pt-3 space-y-3"
                                                onClick={(e) => e.stopPropagation()}
                                            >
                                                <div className="flex items-center justify-between gap-3">
                                                    <label className="text-sm font-medium text-gray-700">
                                                        Document obligatoire
                                                    </label>

                                                    <input
                                                        type="checkbox"
                                                        checked={Boolean(sel.mandatory)}
                                                        onChange={(e) =>
                                                            updateRequiredDoc(doc._id, { mandatory: e.target.checked })
                                                        }
                                                        className="h-4 w-4"
                                                    />
                                                </div>

                                                <div className="rounded border bg-gray-50 p-3 space-y-2">
                                                    <label className="text-sm font-medium">Afficher selon une réponse</label>
                                                    <select className="w-full border rounded px-3 py-2 text-sm" value={sel.conditionalDisplay?.fieldKey || ''}
                                                        onChange={e => updateRequiredDoc(doc._id, { conditionalDisplay: e.target.value ? { fieldKey: e.target.value, operator: 'equals', value: '' } : undefined })}>
                                                        <option value="">Toujours afficher</option>
                                                        {(service.requiredFields || []).filter(field => field.type !== 'file').map(field => <option key={field.key} value={field.key}>{field.label?.[lang] || field.label?.fr || field.key}</option>)}
                                                    </select>
                                                    {sel.conditionalDisplay?.fieldKey && <>
                                                        <select className="w-full border rounded px-3 py-2 text-sm" value={sel.conditionalDisplay.operator || 'equals'} onChange={e => updateRequiredDoc(doc._id, { conditionalDisplay: { ...sel.conditionalDisplay, operator: e.target.value } })}>
                                                            <option value="equals">Est égal à</option><option value="not_equals">Est différent de</option>
                                                        </select>
                                                        <input className="w-full border rounded px-3 py-2 text-sm" placeholder="Valeur exacte de la réponse, ex : Non (false pour une case décochée)" value={String(sel.conditionalDisplay.value ?? '')} onChange={e => updateRequiredDoc(doc._id, { conditionalDisplay: { ...sel.conditionalDisplay, value: e.target.value } })} />
                                                        <p className="text-xs text-gray-500">Le document sera affiché lorsque la condition est remplie. Cochez « Document obligatoire » pour imposer son envoi.</p>
                                                    </>}
                                                </div>

                                                <div>
                                                    <div className="flex items-center justify-between gap-3 mb-2">
                                                        <label className="text-sm font-medium text-gray-700">
                                                            Affichage selon le sous-domaine
                                                        </label>

                                                        <button
                                                            type="button"
                                                            onClick={() => setRequiredDocAllSubdomains(doc._id)}
                                                            className={`text-xs rounded px-2 py-1 border ${isAllSubdomains
                                                                ? 'bg-blue-600 text-white border-blue-600'
                                                                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                                                                }`}
                                                        >
                                                            Tous
                                                        </button>
                                                    </div>

                                                    <p className="text-xs text-gray-500 mb-2">
                                                        Si aucun sous-domaine n’est sélectionné, le document sera demandé partout.
                                                    </p>

                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                                        {subdomainOptions.map((option) => (
                                                            <label
                                                                key={option.value}
                                                                className={`flex items-center gap-2 rounded border px-3 py-2 text-sm ${selectedSubdomains.includes(option.value)
                                                                    ? 'bg-blue-50 border-blue-400 text-blue-800'
                                                                    : 'bg-white border-gray-200 text-gray-700'
                                                                    }`}
                                                            >
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedSubdomains.includes(option.value)}
                                                                    onChange={() =>
                                                                        toggleRequiredDocSubdomain(doc._id, option.value)
                                                                    }
                                                                    className="h-4 w-4"
                                                                />

                                                                <span>{option.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
            )}
            {activeTab === 'fields' && (
                <div className="rounded-2xl border bg-white shadow-sm p-5">
                    <FieldSelector
                        value={service.requiredFields || []}
                        onChange={(fields) => updateField('requiredFields', fields)}
                        lang={lang}
                        langs={['fr', 'en', 'de']}
                        subdomains={subdomains}
                    />
                </div>
            )}
            {activeTab === 'email' && (
                <div className="rounded-2xl border bg-white shadow-sm p-5 space-y-5">
                    {/* Global fallback (optional) */}
                    <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="text-sm text-gray-600">
                            <a href="/email-templates" className="text-blue-600 hover:underline">Gérer les modèles</a>
                            <span className="mx-2 text-gray-300">•</span>
                            <a href="/email-templates/new" className="text-blue-600 hover:underline">Créer un modèle</a>
                        </div>
                    </div>

                    {/* Per-status mapping */}
                    <div className="grid gap-4 md:grid-cols-2">
                        {TRIGGERS.map(tr => (
                            <div key={tr}>
                                <label className="block mb-1 text-sm font-medium text-gray-700">{trigLabel(tr)}</label>
                                <select
                                    value={(service.emailTemplates?.[tr]) || ''}
                                    onChange={(e) => updateField('emailTemplates', {
                                        ...(service.emailTemplates || {}),
                                        [tr]: e.target.value || undefined
                                    })}
                                    className="w-full border rounded px-3 py-2 shadow-sm"
                                >
                                    <option value="">— Utiliser le global / défaut —</option>
                                    {templatesFor(tr).map((tpl) => (
                                        <option key={tpl._id} value={tpl._id}>
                                            {tpl.name?.[lang] || tpl.name?.fr || tpl.name?.en || tpl.name?.de || '(Sans nom)'}
                                        </option>
                                    ))}
                                </select>
                                <p className="text-xs text-gray-500 mt-1">N’affiche que les modèles au déclencheur « {trigLabel(tr)} » ou « Toujours ».</p>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export default ServiceEditor;